import { useEffect, useState } from 'react';

import { useTranslation } from '@gem/modules/i18n';

interface IExpandableTextBlockPresenterParams {
  fullText: string;
  showMoreText?: string;
  textMaxLength?: number;
}

interface IExpandableTextBlockPresenter {
  shortTextLength: number;
  text: string;
  showMoreText: string;
  hasMore: boolean;
  isFullTextVisible: boolean;

  onShowMore(): void;
}

export const EXPANDABLE_TEXT_MAX_LENGTH = 220;
export const ELLIPSIS = '...';

export function useExpandableTextBlockPresenter(
  params: IExpandableTextBlockPresenterParams
): IExpandableTextBlockPresenter {
  const { fullText, textMaxLength = EXPANDABLE_TEXT_MAX_LENGTH } = params;
  const [isFullTextVisible, setIsFullTextVisible] = useState(false);

  const { t } = useTranslation();

  const presenter: IExpandableTextBlockPresenter = {
    isFullTextVisible,

    get showMoreText(): string {
      return params.showMoreText || t('common_more');
    },

    get shortTextLength(): number {
      return textMaxLength - ELLIPSIS.length - presenter.showMoreText.length;
    },

    get text(): string {
      if (!presenter.hasMore) {
        return fullText;
      }

      return presenter.isFullTextVisible
        ? fullText
        : `${fullText.slice(0, presenter.shortTextLength).trim()}${ELLIPSIS} `;
    },

    get hasMore(): boolean {
      return fullText.trim().length > textMaxLength && !presenter.isFullTextVisible;
    },

    onShowMore(): void {
      if (!presenter.hasMore) {
        return;
      }
      setIsFullTextVisible(true);
    },
  };

  useEffect(() => {
    setIsFullTextVisible(false);
  }, [fullText, textMaxLength]);

  return presenter;
}
